// Libraries
import { Box, Slider as ChakraSlider, SliderMark, SliderThumb, SliderTrack } from '@chakra-ui/react';
// Components
import { CustomMark } from '../CustomMark';
import { CustomSliderProps } from './CustomSlider.types';

/**
 * A custom-styled slider component that provides user interactivity for selecting a range of values.
 *
 * @component
 * @param {CustomSliderProps} props - The props for the CustomSlider component.
 * @param {number} [props.initialValue] - The initial value for the slider's position.
 * @param {boolean} props.isDisabled - Determines if the slider is disabled.
 * @param {number} [props.max=2] - The maximum allowable value for the slider.
 * @param {number} [props.min=1] - The minimum allowable value for the slider.
 * @param {Function} props.setValue - Callback fired when the slider's value changes.
 * @param {number} props.step - The stepping interval for the slider adjustments.
 * @param {number} props.value - Current value position of the slider.
 *
 * @returns {ReactElement} The CustomSlider component.
 */
export const CustomSlider = ({
  initialValue,
  isDisabled,
  max = 2,
  min = 1,
  setValue,
  step,
  value,
}: CustomSliderProps) => {
  const mid = Math.floor(min + (max - min) / 2);
  const changeHandler = (value: number) => setValue(`${value}`);
  return (
    <ChakraSlider
      max={max}
      min={min}
      step={step}
      isDisabled={isDisabled}
      className='slider'
      focusThumbOnChange={false}
      value={value}
      onChange={changeHandler}
    >
      <SliderTrack />
      <SliderThumb />
      {!!initialValue && (
        <SliderMark value={initialValue}>
          <Box position='absolute' left='-3px' top='1px' boxSize={1.5} bg='tealishBlue' />
        </SliderMark>
      )}
      <CustomMark label={min} />
      <CustomMark label={mid} />
      <CustomMark label={max} />
    </ChakraSlider>
  );
};
